type RecentItem = { id: string; label: string; timestamp: number };

interface RecentStoreShape {
  items: RecentItem[];
  maxItems: number;
  ttlMs: number;
}

const DEFAULT_MAX = 8;
const DEFAULT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

function getStorageKey(recentKey: string) {
  return `recentSelections_${recentKey}`;
}

function isBrowser() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

export function getRecents(recentKey: string): RecentItem[] {
  if (!isBrowser()) return [];
  try {
    const raw = window.localStorage.getItem(getStorageKey(recentKey));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Partial<RecentStoreShape> | null;
    if (!parsed || !Array.isArray(parsed.items)) return [];
    const ttlMs = typeof parsed.ttlMs === "number" ? parsed.ttlMs : DEFAULT_TTL_MS;
    const cutoff = Date.now() - ttlMs;
    const filtered = parsed.items.filter((i) => i && typeof i.id === "string" && i.timestamp >= cutoff);
    if (filtered.length !== parsed.items.length) {
      saveRecents(recentKey, filtered, parsed.maxItems ?? DEFAULT_MAX, ttlMs);
    }
    return filtered;
  } catch {
    return [];
  }
}

export function addRecent(recentKey: string, item: { id: string; label: string }, max: number = DEFAULT_MAX) {
  if (!isBrowser()) return;
  const existing = getRecents(recentKey);
  const deduped = existing.filter((i) => i.id !== item.id);
  const updated: RecentItem[] = [{ id: item.id, label: item.label, timestamp: Date.now() }, ...deduped].slice(0, Math.max(1, max));
  saveRecents(recentKey, updated, max, DEFAULT_TTL_MS);
}

export function clearRecents(recentKey: string) {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(getStorageKey(recentKey));
  } catch {
    // no-op
  }
}

function saveRecents(recentKey: string, items: RecentItem[], maxItems: number, ttlMs: number) {
  if (!isBrowser()) return;
  const payload: RecentStoreShape = { items, maxItems, ttlMs };
  try {
    window.localStorage.setItem(getStorageKey(recentKey), JSON.stringify(payload));
  } catch {
    // no-op
  }
}

export type { RecentItem };
